import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/context.tsx';

/**
 * Global toast notification system for surfacing mutation failures (and
 * the occasional success). Previously each page swallowed mutation
 * errors or surfaced them inline-only; once a form collapses (modal
 * closes, row leaves viewport) the error disappeared with no trace.
 *
 * We do NOT add a dependency — this is a tiny portal-backed stack with
 * DESIGN.md-aligned chrome (`surface-container-highest` base,
 * `error-container` for destructive surfaces, `primary` for success).
 *
 * Usage:
 *
 *   const toast = useToast();
 *   try { await mut.mutateAsync(...); toast.success('Revoked'); }
 *   catch (err) { toast.error(coerceMessage(err)); }
 *
 * The provider mounts once under the SessionProvider in App.tsx so the
 * portal outlives any page transition.
 */

export type ToastVariant = 'error' | 'success' | 'info';

export interface Toast {
  readonly id: string;
  readonly variant: ToastVariant;
  readonly message: string;
}

interface ToastContextValue {
  readonly toasts: readonly Toast[];
  readonly push: (variant: ToastVariant, message: string) => string;
  readonly dismiss: (id: string) => void;
  readonly error: (message: string) => string;
  readonly success: (message: string) => string;
  readonly info: (message: string) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TTL_MS = 6000;

interface ProviderProps {
  readonly children: ReactNode;
  readonly ttlMs?: number;
}

export function ToastProvider({ children, ttlMs = DEFAULT_TTL_MS }: ProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string): string => {
      const id = `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, variant, message }]);
      const handle = setTimeout(() => dismiss(id), ttlMs);
      timers.current.set(id, handle);
      return id;
    },
    [dismiss, ttlMs],
  );

  const error = useCallback((message: string): string => push('error', message), [push]);
  const success = useCallback((message: string): string => push('success', message), [push]);
  const info = useCallback((message: string): string => push('info', message), [push]);

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      error,
      success,
      info,
    }),
    [toasts, push, dismiss, error, success, info],
  );

  useEffect(() => {
    return () => {
      for (const handle of timers.current.values()) {
        clearTimeout(handle);
      }
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

interface ViewportProps {
  readonly toasts: readonly Toast[];
  readonly onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ViewportProps): JSX.Element | null {
  const t = useT();
  if (typeof document === 'undefined') {
    return null;
  }
  if (toasts.length === 0) {
    return null;
  }
  return createPortal(
    <div
      role="region"
      aria-label={t('toast.region_aria')}
      data-testid="toast-viewport"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex max-w-[calc(100vw-48px)] flex-col items-end gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastItemProps {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps): JSX.Element {
  const t = useT();
  const skin = variantSkin(toast.variant);
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      data-testid={`toast-${toast.variant}`}
      data-toast-id={toast.id}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl px-4 py-3 shadow-[0_12px_32px_rgba(173,198,255,0.04)] ${skin.container}`}
    >
      <span aria-hidden="true" className={`material-symbols-outlined text-[20px] ${skin.icon}`}>
        {skin.glyph}
      </span>
      <p className={`m-0 flex-1 break-words text-sm leading-snug ${skin.text}`}>{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('toast.dismiss_aria')}
        data-testid="toast-dismiss"
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${skin.dismiss}`}
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
          close
        </span>
      </button>
    </div>
  );
}

interface Skin {
  readonly container: string;
  readonly icon: string;
  readonly text: string;
  readonly glyph: string;
  readonly dismiss: string;
}

function variantSkin(variant: ToastVariant): Skin {
  switch (variant) {
    case 'error':
      return {
        container: 'bg-error-container text-on-error-container',
        icon: 'text-on-error-container',
        text: 'text-on-error-container',
        glyph: 'error',
        dismiss:
          'text-on-error-container hover:bg-error focus-visible:ring-offset-error-container',
      };
    case 'success':
      return {
        container: 'bg-surface-container-highest text-primary',
        icon: 'text-primary',
        text: 'text-primary',
        glyph: 'check_circle',
        dismiss:
          'text-outline hover:bg-surface-bright hover:text-on-surface focus-visible:ring-offset-surface-container-highest',
      };
    default:
      return {
        container: 'bg-surface-container-highest text-on-surface',
        icon: 'text-primary',
        text: 'text-on-surface',
        glyph: 'info',
        dismiss:
          'text-outline hover:bg-surface-bright hover:text-on-surface focus-visible:ring-offset-surface-container-highest',
      };
  }
}

/**
 * Turn any thrown value into a UI-friendly string. Exposed so callers
 * don't have to re-implement the PlatformApiError check each time.
 */
export function coerceErrorMessage(
  err: unknown,
  fallback = 'Network error — please try again',
): string {
  if (err === null || err === undefined) {
    return fallback;
  }
  if (err instanceof Error) {
    const codeHolder = err as Error & { readonly code?: string };
    const code = typeof codeHolder.code === 'string' ? codeHolder.code : null;
    return code !== null && code.length > 0 ? `${code}: ${err.message}` : err.message;
  }
  if (typeof err === 'string') {
    return err.length > 0 ? err : fallback;
  }
  return fallback;
}
